import React, { useCallback } from 'react';

import Pressable from '@atlaskit/primitives/pressable';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import type { Placement } from '@atlaskit/popper';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';

import { FormattedMessage } from '@atlassian/jira-intl';
import { AiWorkCreateLinkPicker } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/ai-work-create-link-picker/index.tsx';
import messages from './messages.tsx';

type Props = {
	onLinkSelected: (link: string) => void;
	isLinkPickerOpen: boolean;
	setIsLinkPickerOpen: (open: boolean) => void;
	placement?: Placement;
};
export function PlaceholderWithLinkPicker({
	onLinkSelected,
	isLinkPickerOpen,
	setIsLinkPickerOpen,
	placement = 'top',
}: Props) {
	const closeLinkPicker = useCallback(() => {
		setIsLinkPickerOpen(false);
	}, [setIsLinkPickerOpen]);

	const openLinkPicker = useCallback(() => {
		setIsLinkPickerOpen(true);
	}, [setIsLinkPickerOpen]);

	const renderLinkPicker = () => (
		<AiWorkCreateLinkPicker onLinkSelected={onLinkSelected} onCloseLinkPicker={closeLinkPicker} />
	);

	return (
		<Stack alignBlock="center" xcss={placeholderStyles}>
			<Inline xcss={placeholderTextStyles}>
				<FormattedMessage
					{...messages.placeholderSmartLinkEditor}
					values={{
						link: (chunk) => (
							<Popup
								messageId="software-backlog.card-list.inline-card-create.ai-work-create.placeholder-with-link-picker.popup"
								messageType="transactional"
								isOpen={isLinkPickerOpen}
								onClose={closeLinkPicker}
								content={renderLinkPicker}
								placement={placement}
								// need to pass in chunk, disabling
								// eslint-disable-next-line jira/react-no-inline-function-prop
								trigger={({ ...triggerProps }) => (
									<Pressable
										data-testId="software-backlog.card-list.inline-card-create.ai-work-create.placeholder-with-link-picker.link"
										paddingInline="space.0"
										xcss={linkPickerPromptStyles}
										onClick={openLinkPicker}
										{...triggerProps}
									>
										{chunk}
									</Pressable>
								)}
							/>
						),
					}}
				/>
			</Inline>
		</Stack>
	);
}
const placeholderStyles = xcss({
	position: 'absolute',
	color: 'color.text.subtlest',
	pointerEvents: 'none',
	zIndex: 'layer',
	height: '100%',
});

const linkPickerPromptStyles = xcss({
	pointerEvents: 'auto',
	color: 'color.link',
	textDecoration: 'underline',
	background: 'none',
});
const placeholderTextStyles = xcss({ whiteSpace: 'pre-wrap' });
